/**
 *  @class
 *  @function UniversalPillars
 */

if (!customElements.get('universal-pillars')) {
  class UniversalPillars extends HTMLElement {
    constructor() {
      super();
      this.tl = false;
      this.observer = null;
      this.animated = false;
    }

    connectedCallback() {
      // Parse heading markers
      this.initMarkers();

      const hasGsap = typeof gsap !== 'undefined';
      const hasScrollTrigger = typeof ScrollTrigger !== 'undefined';

      console.log('[UniversalPillars] Init check:', {
        'gsap available': hasGsap,
        'ScrollTrigger available': hasScrollTrigger
      });

      if (hasGsap && hasScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
        console.log('[UniversalPillars] Using GSAP animations');
        this.prepareAnimations();
      } else {
        console.log('[UniversalPillars] Using vanilla JS fallback');
        this.setupNumberAnimation();
      }
    }

    initMarkers() {
      const headings = this.querySelectorAll('.universal-pillars--heading');
      headings.forEach(heading => {
        heading.innerHTML = heading.innerHTML.replace(/\|([^|]+)\|/g, '<span class="text-marker">$1</span>');
      });
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.tl) {
        this.tl.kill();
      }
    }

    // Fallback animation without GSAP
    setupNumberAnimation() {
      let section = this;
      let numbers = section.querySelectorAll('.universal-pillars--number-box .number');

      console.log('[UniversalPillars] Setting up vanilla animation, found numbers:', numbers.length);

      if (numbers.length === 0) {
        console.log('[UniversalPillars] No number elements found, skipping animation setup');
        return;
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          console.log('[UniversalPillars] Intersection:', entry.isIntersecting, 'animated:', section.animated);
          if (entry.isIntersecting && !section.animated) {
            section.animated = true;
            console.log('[UniversalPillars] Triggering vanilla animation');
            section.animateNumbersVanilla();
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

      this.observer.observe(this);
    }

    parseNumber(originalText) {
      // Remove commas for parsing
      let cleanText = originalText.replace(/,/g, '');
      let numericPart = cleanText.match(/[\d.]+/);
      if (!numericPart) return null;

      let baseNumber = parseFloat(numericPart[0]);
      let multiplier = 1;
      let formatSuffix = '';
      let prefix = originalText.match(/^[^\d]*/)[0] || '';

      // Find where the number ends in the original text (accounting for commas)
      let numberEndIndex = 0;
      let digitCount = 0;
      for (let i = 0; i < originalText.length; i++) {
        if (/[\d,.]/.test(originalText[i])) {
          numberEndIndex = i + 1;
        } else if (digitCount > 0) {
          break;
        }
        if (/\d/.test(originalText[i])) digitCount++;
      }
      let afterNumber = originalText.slice(numberEndIndex);

      if (/k/i.test(afterNumber)) {
        multiplier = 1000;
        formatSuffix = 'K';
        afterNumber = afterNumber.replace(/k/i, '');
      } else if (/m/i.test(afterNumber)) {
        multiplier = 1000000;
        formatSuffix = 'M';
        afterNumber = afterNumber.replace(/m/i, '');
      } else if (/b/i.test(afterNumber)) {
        multiplier = 1000000000;
        formatSuffix = 'B';
        afterNumber = afterNumber.replace(/b/i, '');
      }

      let numericValue = baseNumber * multiplier;
      let hasDecimal = numericPart[0].includes('.');
      let hasCommas = originalText.includes(',');

      return { numericValue, multiplier, formatSuffix, prefix, afterNumber, hasDecimal, hasCommas };
    }

    formatNumber(val, parsed) {
      let displayVal = val / parsed.multiplier;
      let result = parsed.hasDecimal ? displayVal.toFixed(1) : Math.round(displayVal).toString();

      // Add commas back if original had them
      if (parsed.hasCommas) {
        result = result.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }

      return parsed.prefix + result + parsed.formatSuffix + parsed.afterNumber;
    }

    animateNumbersVanilla() {
      let numbers = this.querySelectorAll('.universal-pillars--number-box .number');
      console.log('[UniversalPillars] Vanilla animation triggered, found numbers:', numbers.length);

      numbers.forEach((numberEl, index) => {
        let originalText = numberEl.textContent.trim();
        console.log('[UniversalPillars] Processing number:', originalText);
        let parsed = this.parseNumber(originalText);
        console.log('[UniversalPillars] Parsed result:', parsed);
        if (!parsed || isNaN(parsed.numericValue) || parsed.numericValue <= 0) {
          console.log('[UniversalPillars] Skipping invalid number');
          return;
        }

        let startValue = Math.max(0, parsed.numericValue - 3000);
        let duration = 2000;
        let startTime = null;
        let delay = index * 100;
        let self = this;

        console.log('[UniversalPillars] Animating from', startValue, 'to', parsed.numericValue);

        setTimeout(() => {
          console.log('[UniversalPillars] Starting animation for index', index);
          function animate(currentTime) {
            if (!startTime) startTime = currentTime;
            let elapsed = currentTime - startTime;
            let progress = Math.min(elapsed / duration, 1);
            let easedProgress = 1 - Math.pow(1 - progress, 3);
            let currentValue = startValue + (parsed.numericValue - startValue) * easedProgress;
            numberEl.textContent = self.formatNumber(currentValue, parsed);

            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              console.log('[UniversalPillars] Animation complete for index', index);
            }
          }
          requestAnimationFrame(animate);
        }, delay);
      });
    }

    prepareAnimations() {
      let section = this;

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top center+=100",
          end: "bottom center"
        }
      });

      // Animate pillar items (exclude items with images)
      let items = section.querySelectorAll('.universal-pillars--item:not(:has(.universal-pillars--image))');
      if (items.length > 0) {
        section.tl.to(items, {
          duration: 0.6,
          opacity: 1,
          y: 0,
          stagger: 0.15,
          ease: "power2.out"
        }, 0);
      }

      // Images in items with images don't animate - skip image animation
      let images = [];
      if (images.length > 0) {
        section.tl.to(images, {
          duration: 0.8,
          opacity: 1,
          scale: 1,
          stagger: 0.1,
          ease: "power2.out"
        }, 0.3);
      }

      // Animate numbers with GSAP - each number triggers independently
      let numberItems = section.querySelectorAll('.universal-pillars--number-item');
      let self = this;

      numberItems.forEach((item) => {
        let numberEl = item.querySelector('.number');
        if (!numberEl) return;

        let originalText = numberEl.textContent.trim();
        let parsed = self.parseNumber(originalText);
        if (!parsed || isNaN(parsed.numericValue) || parsed.numericValue <= 0) return;

        // Store final text and measure width to prevent CLS
        let finalText = self.formatNumber(parsed.numericValue, parsed);
        numberEl.textContent = finalText;
        let finalWidth = numberEl.offsetWidth;
        numberEl.style.minWidth = finalWidth + 'px';

        let startValue = Math.max(0, parsed.numericValue - 100000);

        // Set initial value
        numberEl.textContent = self.formatNumber(startValue, parsed);

        let countObj = { val: startValue };

        // Each number has its own ScrollTrigger
        gsap.to(countObj, {
          val: parsed.numericValue,
          duration: 2.5,
          ease: "power1.out",
          scrollTrigger: {
            trigger: item,
            start: "top 80%",
            end: "top 80%",
                        toggleActions: "play none none none"
          },
          onUpdate: function() {
            numberEl.textContent = self.formatNumber(countObj.val, parsed);
          }
        });
      });

      // Big number - continuous YouTube-style increment
      let bigNumbers = section.querySelectorAll('.universal-pillars--big-number');
      bigNumbers.forEach((item) => {
        let numberEl = item.querySelector('.number');
        if (!numberEl) return;

        let originalText = numberEl.textContent.trim();
        let parsed = self.parseNumber(originalText);
        if (!parsed || isNaN(parsed.numericValue) || parsed.numericValue <= 0) return;

        // Store final text and measure width (add extra for growth)
        let finalText = self.formatNumber(parsed.numericValue * 1.1, parsed);
        numberEl.textContent = finalText;
        let finalWidth = numberEl.offsetWidth;
        numberEl.style.minWidth = finalWidth + 'px';

        let startValue = Math.max(0, parsed.numericValue - 100000);
        numberEl.textContent = self.formatNumber(startValue, parsed);

        let countObj = { val: startValue };

        // Initial count-up animation
        gsap.to(countObj, {
          val: parsed.numericValue,
          duration: 2.5,
          ease: "power1.out",
          scrollTrigger: {
            trigger: item,
            start: "top 80%",
            end: "top 80%",
                        toggleActions: "play none none none"
          },
          onUpdate: function() {
            numberEl.textContent = self.formatNumber(countObj.val, parsed);
          },
          onComplete: function() {
            // Start continuous increment after initial animation
            self.startContinuousIncrement(numberEl, countObj, parsed);
          }
        });
      });
    }

    startContinuousIncrement(numberEl, countObj, parsed) {
      let self = this;

      function incrementBatch() {
        // Random increment between 1-5
        let increment = Math.floor(Math.random() * 5) + 1;
        let targetVal = countObj.val + increment;

        gsap.to(countObj, {
          val: targetVal,
          duration: 0.3,
          ease: "power1.out",
          onUpdate: function() {
            numberEl.textContent = self.formatNumber(countObj.val, parsed);
          }
        });

        // Random delay between 500ms - 3000ms for next batch
        let nextDelay = Math.floor(Math.random() * 2500) + 500;
        setTimeout(incrementBatch, nextDelay);
      }

      // Start after a short delay
      setTimeout(incrementBatch, 1000);
    }
  }
  customElements.define('universal-pillars', UniversalPillars);
}
